import { useEffect, useState } from "react";
import {
  connectWhatsappManually,
  debugWhatsappManualToken,
  getWhatsappAccountCredentials,
  previewWhatsappManualConnect,
  updateWhatsappManualCredentials,
} from "../../apis/whatsappAccount";
import SetupStepsList from "./SetupStepsList";
import TokenPermissionsPanel from "./TokenPermissionsPanel";

const STEPS = [
  { id: 1, label: "Credentials" },
  { id: 2, label: "Verify" },
  { id: 3, label: "Activate" },
];

const ManualConnectModal = ({
  open,
  onClose,
  onSuccess,
  onConnectStart,
  onConnectEnd,
  onConnectError,
  editPhoneId = null,
}) => {
  const isEdit = Boolean(editPhoneId);
  const [step, setStep] = useState(1);
  const [wabaId, setWabaId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verifiedName, setVerifiedName] = useState("");
  const [metaStatus, setMetaStatus] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [metaAppId, setMetaAppId] = useState("");
  const [metaAppSecret, setMetaAppSecret] = useState("");
  const [maskedAppSecret, setMaskedAppSecret] = useState("");
  const [hasStoredAppSecret, setHasStoredAppSecret] = useState(false);
  const [maskedToken, setMaskedToken] = useState("");
  const [hasExistingToken, setHasExistingToken] = useState(false);
  const [verifiedFromMeta, setVerifiedFromMeta] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [tokenDebug, setTokenDebug] = useState(null);
  const [error, setError] = useState("");
  const [failedSteps, setFailedSteps] = useState([]);

  const resetForm = () => {
    setStep(1);
    setWabaId("");
    setPhoneNumberId("");
    setPhoneNumber("");
    setVerifiedName("");
    setMetaStatus("");
    setAccessToken("");
    setMetaAppId("");
    setMetaAppSecret("");
    setMaskedAppSecret("");
    setHasStoredAppSecret(false);
    setMaskedToken("");
    setHasExistingToken(false);
    setVerifiedFromMeta(false);
    setTokenDebug(null);
    setError("");
    setFailedSteps([]);
  };

  useEffect(() => {
    if (!open) return;

    setError("");
    setFailedSteps([]);
    setAccessToken("");
    setVerifiedFromMeta(false);
    setTokenDebug(null);

    if (!isEdit) {
      resetForm();
      setStep(1);
      return;
    }

    setStep(1);
    const loadCredentials = async () => {
      try {
        setLoadingCredentials(true);
        const res = await getWhatsappAccountCredentials(editPhoneId);
        const data = res.data?.data;
        if (data) {
          setWabaId(data.waba_id || "");
          setPhoneNumberId(data.phone_id || "");
          setPhoneNumber(data.phone_number || "");
          setVerifiedName(data.verified_name || "");
          setMaskedToken(data.access_token_masked || "");
          setHasExistingToken(Boolean(data.has_access_token));
          setMetaAppId(data.meta_app_id || "");
          setMaskedAppSecret(data.meta_app_secret_masked || "");
          setHasStoredAppSecret(Boolean(data.has_meta_app_secret));
        }
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load account credentials");
      } finally {
        setLoadingCredentials(false);
      }
    };

    loadCredentials();
  }, [open, isEdit, editPhoneId]);

  const applyPreviewData = (data) => {
    if (data.phone_number) setPhoneNumber(data.phone_number);
    if (data.verified_name) setVerifiedName(data.verified_name);
    if (data.waba_id && !isEdit) setWabaId(data.waba_id);
    if (data.waba_name && !wabaId) setWabaId(data.waba_id || wabaId);
    setMetaStatus(data.status || data.code_verification_status || "");
    if (data.token) setTokenDebug(data.token);
    setVerifiedFromMeta(true);
  };

  const metaCredsPayload = () => ({
    metaAppId: metaAppId.trim() || undefined,
    metaAppSecret: metaAppSecret.trim() || undefined,
  });

  const handleVerify = async () => {
    setError("");
    setFailedSteps([]);
    setTokenDebug(null);

    const tokenToUse = accessToken.trim();
    if (!phoneNumberId.trim() || !tokenToUse) {
      setError("Phone Number ID and Access Token are required to verify");
      return;
    }

    try {
      setVerifying(true);
      const res = await previewWhatsappManualConnect({
        phoneNumberId: phoneNumberId.trim(),
        accessToken: tokenToUse,
        wabaId: wabaId.trim() || undefined,
        ...metaCredsPayload(),
      });
      const data = res.data?.data;
      if (data) {
        applyPreviewData(data);
        if (!isEdit) setStep(2);
      }
    } catch (err) {
      setVerifiedFromMeta(false);
      const setupSteps = err.response?.data?.setupSteps;
      if (setupSteps?.length) setFailedSteps(setupSteps);
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Failed to verify credentials with Meta"
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleDebugToken = async () => {
    setError("");
    setTokenDebug(null);

    const tokenToUse = accessToken.trim();
    if (!phoneNumberId.trim()) {
      setError("Phone Number ID is required");
      return;
    }
    if (!tokenToUse && !hasExistingToken) {
      setError("Enter a new access token to debug");
      return;
    }

    try {
      setVerifying(true);
      const res = await debugWhatsappManualToken({
        phoneNumberId: phoneNumberId.trim(),
        accessToken: tokenToUse || undefined,
        syncToDb: false,
        ...metaCredsPayload(),
      });
      const data = res.data?.data;
      if (data) {
        if (data.phone) {
          setPhoneNumber(data.phone.phone_number || phoneNumber);
          setVerifiedName(data.phone.verified_name || verifiedName);
          setMetaStatus(data.phone.status || data.phone.code_verification_status || "");
        }
        if (data.waba_id) setWabaId(data.waba_id);
        if (data.token) setTokenDebug(data.token);
        setVerifiedFromMeta(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details || "Token debug failed");
    } finally {
      setVerifying(false);
    }
  };

  const canGoToActivate = () =>
    wabaId.trim() &&
    phoneNumberId.trim() &&
    phoneNumber.trim() &&
    verifiedName.trim() &&
    accessToken.trim() &&
    verifiedFromMeta;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFailedSteps([]);

    if (isEdit) {
      if (!accessToken.trim()) {
        setError("Enter the new access token to update");
        return;
      }
      if (!verifiedFromMeta) {
        setError("Debug the token first to verify it with Meta");
        return;
      }
    } else {
      if (!wabaId.trim() || !phoneNumberId.trim()) {
        setError("WABA ID and Phone Number ID are required");
        return;
      }
      if (!phoneNumber.trim() || !verifiedName.trim()) {
        setError("Verify credentials to fetch phone number and verified name from Meta");
        return;
      }
      if (!accessToken.trim()) {
        setError("Access token is required");
        return;
      }
    }

    onConnectStart?.();
    onClose();

    try {
      const res = isEdit
        ? await updateWhatsappManualCredentials({
            phoneNumberId: phoneNumberId.trim(),
            accessToken: accessToken.trim(),
            ...metaCredsPayload(),
          })
        : await connectWhatsappManually({
            phoneNumberId: phoneNumberId.trim(),
            wabaId: wabaId.trim(),
            phoneNumber: phoneNumber.trim(),
            verifiedName: verifiedName.trim(),
            accessToken: accessToken.trim(),
            ...metaCredsPayload(),
          });

      onSuccess?.(res.data);
    } catch (err) {
      const data = err.response?.data || {};
      onConnectError?.({
        type: "error",
        title: data.error_title || (isEdit ? "Update failed" : "Could not connect WhatsApp"),
        message:
          data.message ||
          data.error ||
          data.details ||
          "Failed to save WhatsApp credentials",
        setupSteps: data.setupSteps || [],
      });
    } finally {
      onConnectEnd?.();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "Update Access Token" : "Connect Manually"}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {isEdit
              ? "Paste a new token — we fetch all details from Meta and update the database."
              : "We verify with Meta, then activate the number on Blutec Ping automatically — no PIN needed."}
          </p>

          {!isEdit && (
            <div className="flex items-center gap-2 mt-4">
              {STEPS.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      step >= s.id
                        ? "bg-[#eefa94] text-gray-900 border border-gray-300"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {s.id}
                  </div>
                  <span className={`text-xs font-medium hidden sm:inline ${step >= s.id ? "text-gray-900" : "text-gray-400"}`}>
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 ${step > s.id ? "bg-[#eefa94]" : "bg-gray-200"}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {loadingCredentials ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-gray-900 rounded-full" />
              Loading credentials…
            </div>
          ) : (
            <>
              {(isEdit || step === 1) && (
                <div className="space-y-4">
                  {!isEdit && (
                    <>
                      <div>
                        <label className="field-label">WABA ID</label>
                        <input
                          type="text"
                          value={wabaId}
                          onChange={(e) => setWabaId(e.target.value)}
                          placeholder="e.g. 979680285091234"
                          className="field-input"
                        />
                      </div>
                      <div>
                        <label className="field-label">Phone Number ID</label>
                        <input
                          type="text"
                          value={phoneNumberId}
                          onChange={(e) => {
                            setPhoneNumberId(e.target.value);
                            setVerifiedFromMeta(false);
                            setTokenDebug(null);
                          }}
                          placeholder="e.g. 1221362797732075"
                          className="field-input"
                        />
                      </div>
                    </>
                  )}

                  {isEdit && (
                    <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm space-y-1.5">
                      <p><span className="text-gray-500">Phone ID:</span> <span className="font-mono">{phoneNumberId}</span></p>
                      <p><span className="text-gray-500">WABA ID:</span> <span className="font-mono">{wabaId}</span></p>
                      <p><span className="text-gray-500">Number:</span> {phoneNumber || "—"}</p>
                      <p><span className="text-gray-500">Verified name:</span> {verifiedName || "—"}</p>
                    </div>
                  )}

                  <div>
                    <label className="field-label">
                      {isEdit ? "New Access Token" : "Access Token"}
                    </label>
                    {isEdit && hasExistingToken && !accessToken && (
                      <p className="text-xs text-gray-500 mb-1.5 font-mono">Current: {maskedToken || "****"}</p>
                    )}
                    <input
                      type="password"
                      value={accessToken}
                      onChange={(e) => {
                        setAccessToken(e.target.value);
                        setVerifiedFromMeta(false);
                        setTokenDebug(null);
                      }}
                      placeholder={isEdit ? "Paste new permanent token from Meta" : "Permanent token from Meta"}
                      autoComplete="off"
                      className="field-input font-mono"
                    />
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        Token debug only (optional)
                      </p>
                      <p className="text-xs text-gray-500">
                        Meta App ID + Secret are only used for permission debug — not required for connect, messaging, or template sync.
                      </p>
                      <div>
                        <label className="field-label">Meta App ID</label>
                        <input
                          type="text"
                          value={metaAppId}
                          onChange={(e) => {
                            setMetaAppId(e.target.value);
                            setTokenDebug(null);
                          }}
                          placeholder="App that issued your access token"
                          className="field-input font-mono"
                        />
                      </div>
                      <div>
                        <label className="field-label">Meta App Secret</label>
                        {hasStoredAppSecret && !metaAppSecret && (
                          <p className="text-xs text-gray-500 mb-1.5 font-mono">
                            Saved: {maskedAppSecret || "****"}
                          </p>
                        )}
                        <input
                          type="password"
                          value={metaAppSecret}
                          onChange={(e) => {
                            setMetaAppSecret(e.target.value);
                            setTokenDebug(null);
                          }}
                          placeholder={
                            hasStoredAppSecret
                              ? "Leave blank to keep existing secret"
                              : "App Secret from Meta Developer Console"
                          }
                          autoComplete="off"
                          className="field-input font-mono"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={isEdit ? handleDebugToken : handleVerify}
                      disabled={
                        verifying ||
                        !phoneNumberId.trim() ||
                        (!isEdit && !accessToken.trim()) ||
                        (isEdit && !accessToken.trim() && !hasExistingToken)
                      }
                      className="mt-2 w-full btn-secondary"
                    >
                      {verifying
                        ? "Checking with Meta…"
                        : isEdit
                        ? "Debug Token & Fetch Details"
                        : "Verify & Continue →"}
                    </button>
                  </div>

                  {tokenDebug && <TokenPermissionsPanel tokenDebug={tokenDebug} compact />}
                </div>
              )}

              {!isEdit && step === 2 && (
                <div className="space-y-4">
                  {verifiedFromMeta && (
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                      <p className="text-sm font-semibold text-emerald-800">Verified with Meta</p>
                      {metaStatus && (
                        <p className="text-xs text-emerald-700 mt-1">Status: {metaStatus}</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="field-label">Phone Number</label>
                    <input type="text" value={phoneNumber} readOnly className="field-input bg-gray-50" />
                  </div>
                  <div>
                    <label className="field-label">Verified Name</label>
                    <input type="text" value={verifiedName} readOnly className="field-input bg-gray-50" />
                  </div>
                  {tokenDebug && <TokenPermissionsPanel tokenDebug={tokenDebug} compact />}
                  <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-600 hover:text-gray-900">
                    ← Back to credentials
                  </button>
                </div>
              )}

              {(isEdit || step === 2 || step === 3) && (
                <div className={!isEdit && step === 2 ? "border-t border-gray-100 pt-4 space-y-4" : "space-y-4"}>
                  {(!isEdit && step === 2) && (
                    <button
                      type="button"
                      onClick={() => canGoToActivate() && setStep(3)}
                      disabled={!canGoToActivate()}
                      className="w-full btn-primary"
                    >
                      Continue to activation →
                    </button>
                  )}

                  {(isEdit || step === 3) && (
                    <>
                      {!isEdit && (
                        <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
                          <p><span className="text-gray-500">WABA:</span> {wabaId}</p>
                          <p><span className="text-gray-500">Phone:</span> {phoneNumber}</p>
                          <p><span className="text-gray-500">Name:</span> {verifiedName}</p>
                        </div>
                      )}
                      {!isEdit && (
                        <p className="text-xs text-gray-500">
                          On connect, the number is activated on Blutec Ping automatically — no
                          two-step PIN or Cloud API registration required.
                        </p>
                      )}
                      {!isEdit && (
                        <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-600 hover:text-gray-900">
                          ← Back
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2">
              <p className="text-sm text-red-800 font-medium">{error}</p>
              {failedSteps.length > 0 && <SetupStepsList steps={failedSteps} compact />}
            </div>
          )}
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 btn-secondary">
            Cancel
          </button>
          {(isEdit || step === 3) && !loadingCredentials && (
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isEdit && (!accessToken.trim() || !verifiedFromMeta)}
              className="flex-1 btn-primary disabled:opacity-50"
            >
              {isEdit ? "Update Token" : "Connect & Activate"}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .field-label { display: block; font-size: 0.75rem; font-weight: 600; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.375rem; }
        .field-input { width: 100%; padding: 0.625rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.75rem; font-size: 0.875rem; }
        .field-input:focus { outline: none; ring: 2px; box-shadow: 0 0 0 2px rgba(23,24,27,0.15); }
        .btn-primary { padding: 0.625rem 1rem; border-radius: 0.75rem; font-size: 0.875rem; font-weight: 600; border: 1px solid #d1d5db; background: #eefa94; color: #111; }
        .btn-primary:disabled { opacity: 0.5; }
        .btn-secondary { padding: 0.625rem 1rem; border-radius: 0.75rem; font-size: 0.875rem; font-weight: 600; border: 1px solid #d1d5db; background: white; color: #374151; }
        .btn-secondary:disabled { opacity: 0.5; }
      `}</style>
    </div>
  );
};

export default ManualConnectModal;
